/**
 * Analytics Module
 * 
 * This module provides standardized interfaces and implementations for tracking
 * user interactions within the application. It supports Google Analytics tracking
 * through a standardized API.
 */

// Export event categories enum
export enum EventCategory {
  Navigation = 'Navigation',
  Service = 'Service',
  Contact = 'Contact',
  Engagement = 'Engagement',
  // Restore previously existing categories
  Survey = 'Survey',
  Auth = 'Authentication',
  Admin = 'Admin'
}

// Define the event data structure
export interface AnalyticsEvent {
  category: string;
  action: string;
  label: string;
  value?: number;
}

// Safe way to check environment for browser compatibility
const isDevelopment = import.meta.env.DEV;
const debugAnalytics = import.meta.env.VITE_DEBUG_ANALYTICS === 'true';

// Global configuration
let debugMode = isDevelopment && debugAnalytics;

/**
 * Set debug mode for analytics
 */
export function setDebugMode(value: boolean): void {
  debugMode = value;
}

/**
 * Get current debug mode status
 */
export function isDebugMode(): boolean {
  return debugMode;
}

/**
 * Track an analytics event
 * This is the core tracking function that sends events to Google Analytics
 */
export function trackEvent({ category, action, label, value }: AnalyticsEvent): void {
  // Skip tracking in server environment
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // Debug mode logging
    if (debugMode) {
      console.group('📊 Analytics Event Tracked');
      console.log('Category:', category);
      console.log('Action:', action);
      console.log('Label:', label);
      if (value !== undefined) console.log('Value:', value);
      console.groupEnd();
    }

    // Check if Google Analytics is loaded
    if (typeof window.gtag !== 'function') {
      if (debugMode) {
        console.warn('Google Analytics not found. Event not tracked.');
      }
      return;
    }

    // Check for analytics consent
    const consentKey = 'cookie-consent';
    const savedConsent = localStorage.getItem(consentKey);
    const hasAnalyticsConsent = savedConsent ? JSON.parse(savedConsent).analytics : false;

    // Only send events if user has given analytics consent
    if (hasAnalyticsConsent) {
      // Send event to Google Analytics
      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value
      });
    } else if (debugMode) {
      console.warn('Analytics event not sent: User has not provided consent');
    }
  } catch (error) {
    console.error('Error tracking analytics event:', error);
  }
}

/**
 * Initialize analytics
 * This should be called as early as possible in the application lifecycle
 */
export function initializeAnalytics(): void {
  // Skip in server environment
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // Import and initialize the analytics queue system
    import('./analytics-utils').then(({ initAnalytics }) => {
      initAnalytics();
    }).catch(err => {
      console.error('Failed to initialize analytics queue:', err);
    });

    // Check if debug mode is enabled
    if (debugMode) {
      console.info('Analytics debug mode is enabled');
    }
  } catch (error) {
    console.error('Error initializing analytics:', error);
  }
}

/**
 * Debug function to check analytics consent status
 * This is useful for diagnosing issues with consent-based tracking
 */
export function debugAnalyticsConsent(): void {
  // Skip in server environment
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const consentKey = 'cookie-consent';
    const savedConsent = localStorage.getItem(consentKey);
    const consentObject = savedConsent ? JSON.parse(savedConsent) : { analytics: false, advertising: false };
    
    console.group('📊 Analytics Consent Debug');
    console.log('Consent stored in localStorage:', Boolean(savedConsent));
    console.log('Consent object:', consentObject);
    console.log('Analytics consent granted:', consentObject.analytics);
    console.log('Advertising consent granted:', consentObject.advertising);
    
    // Check if Google Analytics is properly initialized
    console.log('Google Analytics available:', typeof window.gtag === 'function');
    
    // Check Google's consent state if gtag is available
    if (typeof window.gtag === 'function') {
      console.log('Note: GA4 Consent Mode is managed separately from our localStorage consent');
      console.log('Use browser devtools to inspect network requests to analytics endpoints');
    }
    
    console.groupEnd();
  } catch (error) {
    console.error('Error debugging analytics consent:', error);
  }
}